import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useRef, useState } from 'react';

import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogTitle } from 'primitives/Dialog';

import { FormInput, FormPanel } from 'components/FormPanel';

const UPLOAD_MANUALLY_SIGNED_UNIT_TRANSFER_DOCUMENT_MUTATION = gql(`
  mutation UploadManuallySignedUnitTransferDocument($signatureRequestId: ID!, $file: Upload!) {
    uploadManuallySignedUnitTransferDocument(signatureRequestId: $signatureRequestId, file: $file) {
      id
      status
      signedFileUrl
      completedAt
      signers {
        id
        status
        signedAt
      }
    }
  }
`);

// Ops fallback for when a signer hands ops a physical/scanned copy directly instead of uploading
// it themselves via investor-app — same mutation either way, permission is checked server-side.
const UploadManuallySignedDocumentDialogButton: FC<{ signatureRequestId: string }> = ({
  signatureRequestId,
}) => {
  const [showDialog, toggleDialog] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [uploadSignedDocument, { loading, error }] = useMutation(
    UPLOAD_MANUALLY_SIGNED_UNIT_TRANSFER_DOCUMENT_MUTATION
  );

  return (
    <>
      <Button variant="secondary" onClick={() => toggleDialog(true)}>
        Upload Signed Document
      </Button>
      <Dialog open={showDialog} onClose={() => toggleDialog(false)}>
        <DialogPanel>
          <DialogTitle>Upload Signed Document</DialogTitle>
          <p className="mb-4 text-sm text-gray-600">
            Upload the final, fully-signed copy of this document. This marks the whole document
            signed — for a multi-signer document, this should be the single combined file after
            every party has signed it.
          </p>
          <FormPanel
            loading={loading}
            error={error}
            onCancel={() => toggleDialog(false)}
            onSubmit={data =>
              uploadSignedDocument({ variables: { signatureRequestId, file: data.file } }).then(
                () => toggleDialog(false)
              )
            }
            buttonRef={buttonRef}
          >
            <FormInput type="upload" fieldName="file" label="Signed file" defaultValue="" />
          </FormPanel>
          <DialogActions>
            <Button
              variant="secondary"
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.cancel();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.submit();
              }}
              loading={loading}
            >
              Submit
            </Button>
          </DialogActions>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default UploadManuallySignedDocumentDialogButton;
